import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { SubCategoryItem } from "./SubCategoryItem";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type CategoryItemProps = {
  category: {
    id: number;
    name: string;
    index: string;
    title?: string;
    description?: string;
    product_count?: string;
    url?: string;
    cat_text?: string;
  };
  isExpanded: boolean;
  expandedChildCategories: string[];
  onToggle: () => void;
  onChildToggle: (categoryName: string) => void;
  selectedUpdate: string;
};

export const CategoryItem = ({ 
  category, 
  isExpanded, 
  expandedChildCategories,
  onToggle,
  onChildToggle,
  selectedUpdate,
}: CategoryItemProps) => {
  const { data: childCategories } = useQuery({
    queryKey: ["child-categories", category.name, isExpanded, selectedUpdate],
    enabled: isExpanded,
    queryFn: async () => {
      let query = supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq("level", 2)
        .eq("parent_node_name", category.name);

      if (selectedUpdate && selectedUpdate !== "all") {
        const [hour, date] = selectedUpdate.split('h ');
        const [day, month, year] = date.split('-');
        const startDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour)
        );
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-4 p-4 rounded-lg bg-card hover:bg-muted/50 transition-colors cursor-pointer">
          <ChevronRight
            className={`h-5 w-5 transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="font-medium">
                  {category.url ? (
                    <a 
                      href={category.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {category.name}
                    </a>
                  ) : (
                    category.name
                  )}
                </div>
                <Badge 
                  variant={category.index === "index, follow" ? "default" : "destructive"}
                >
                  {category.index}
                </Badge>
                <Badge
                  variant={category.cat_text ? "default" : "secondary"}
                  className={category.cat_text ? "bg-green-500" : "bg-red-200"}
                >
                  {category.cat_text ? "Found content" : "no content"}
                </Badge>
              </div>
              {category.product_count && (
                <div className="text-sm text-muted-foreground">
                  Products: {category.product_count}
                </div>
              )}
            </div>
            {category.title && (
              <p className="text-sm text-muted-foreground text-left font-medium">
                {category.title}
              </p>
            )}
            {category.description && (
              <p className="text-sm text-muted-foreground text-left">
                {category.description}
              </p>
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-8 space-y-2 mt-2">
          {childCategories?.map((child) => (
            <SubCategoryItem
              key={child.id}
              child={child}
              isExpanded={expandedChildCategories.includes(child.name)}
              onToggle={() => onChildToggle(child.name)}
              selectedUpdate={selectedUpdate}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};